import React, { useEffect, useState } from "react";
import api, { API, formatIDR } from "../lib/api";
import Layout from "../components/Layout";
import { Lock, RefreshCw, Save, Banknote, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import ReimburseGallery, { ReimburseFlags, reimburseRowTone } from "./ReimburseGallery";

const STATUS_OPTS = [
  { v: "masuk", l: "Masuk" },
  { v: "libur", l: "Libur" },
  { v: "telat", l: "Telat" },
  { v: "tidak_absen", l: "Tidak Absen" },
];
const TELAT_UANG_HADIR = 5000;

const monthNow = () => new Date().toISOString().slice(0, 7);

const Payroll = () => {
  const [locked, setLocked] = useState(true);
  const [lockPwd, setLockPwd] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [techs, setTechs] = useState([]);
  const [techId, setTechId] = useState("");
  const [month, setMonth] = useState(monthNow());
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [manual, setManual] = useState({ bonus_lainnya: 0, bon_bensin: 0, bonus_dok_report: 0 });
  const [totalVideo, setTotalVideo] = useState(0);
  const [videoPlatforms, setVideoPlatforms] = useState(["youtube", "short", "tiktok", "instagram"]);
  const [reimburseIds, setReimburseIds] = useState([]);
  const [bensinSource, setBensinSource] = useState("reimburse");
  const [potongan, setPotongan] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showVideoDetail, setShowVideoDetail] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const onReimburseRejected = (rid) => {
    const list = (data?.reimbursements || []).filter((x) => x.id !== rid);
    const nextIds = reimburseIds.filter((x) => x !== rid);
    setData((d) => ({ ...d, reimbursements: list }));
    setReimburseIds(nextIds);
    if (bensinSource === "reimburse") {
      const sum = list.filter((x) => nextIds.includes(x.id)).reduce((a, x) => a + (Number(x.amount) || 0), 0);
      setManual((m) => ({ ...m, bon_bensin: sum }));
    }
  };

  useEffect(() => {
    if (!locked) api.get("/technicians").then((r) => setTechs(r.data));
  }, [locked]);

  const submitUnlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    try {
      await api.post("/settings/unlock", { password: lockPwd });
      setLocked(false);
      setLockPwd("");
      toast.success("Akses payroll terbuka");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password salah");
    } finally {
      setUnlocking(false);
    }
  };

  const applyResponse = (d) => {
    setData((prev) => ({
      ...d,
      bonus_dok_auto: d.bonus_dok_auto !== undefined ? d.bonus_dok_auto : prev?.bonus_dok_auto || 0,
      outside_base: d.outside_base !== undefined ? d.outside_base : prev?.outside_base || null,
      bonus_dok_breakdown: d.bonus_dok_breakdown !== undefined ? d.bonus_dok_breakdown : prev?.bonus_dok_breakdown || [],
      video_counts: d.video_counts !== undefined ? d.video_counts : prev?.video_counts || {},
      reimbursements: d.reimbursements !== undefined ? d.reimbursements : prev?.reimbursements || [],
      bon_bensin_mileage: d.bon_bensin_mileage !== undefined ? d.bon_bensin_mileage : prev?.bon_bensin_mileage || 0,
    }));
    if (d.video_platforms) setVideoPlatforms(d.video_platforms);
    if (d.reimburse_ids) setReimburseIds(d.reimburse_ids);
    if (d.bon_bensin_source) setBensinSource(d.bon_bensin_source);
    setRows(d.rows.map((r) => ({ ...r })));
    setManual({
      bonus_lainnya: d.summary.bonus_lainnya, bon_bensin: d.summary.bon_bensin, bonus_dok_report: d.summary.bonus_dok_report,
    });
    setPotongan(d.summary.potongan);
    setTotalVideo(d.summary.total_video || 0);
  };

  const load = async (refresh = 0) => {
    if (!techId) { toast.error("Pilih karyawan dulu"); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get("/payroll", { params: { technician_id: techId, month, refresh } });
      applyResponse(d);
      if (refresh) toast.success("Data dimuat ulang dari absensi & order");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat payroll");
    } finally {
      setLoading(false);
    }
  };

  const save = async (recordKasbon = false) => {
    setSaving(true);
    try {
      const payload = {
        rows: rows.map(({ date, jam_masuk, jam_pulang, status, points, luar_kota, remark, uang_makan_manual }) => ({
          date, jam_masuk, jam_pulang: jam_pulang || "", status, points: Number(points) || 0, luar_kota: !!luar_kota, remark,
          uang_makan_manual: uang_makan_manual === null || uang_makan_manual === undefined || uang_makan_manual === "" ? null : Number(uang_makan_manual) || 0,
        })),
        bonus_lainnya: Number(manual.bonus_lainnya) || 0,
        bon_bensin: Number(manual.bon_bensin) || 0,
        bonus_dok_report: Number(manual.bonus_dok_report) || 0,
        total_video: Number(totalVideo) || 0,
        video_platforms: videoPlatforms,
        reimburse_ids: reimburseIds,
        bon_bensin_source: bensinSource,
        potongan: Number(potongan) || 0,
        record_kasbon: recordKasbon,
      };
      const { data: d } = await api.put(`/payroll/${techId}/${month}`, payload);
      applyResponse(d);
      toast.success(recordKasbon ? "Payroll disimpan & potongan kasbon dicatat" : "Payroll disimpan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan payroll");
    } finally {
      setSaving(false);
    }
  };

  const ob = data?.outside_base;
  const inPlacement = (d) => !!ob && (!ob.start || ob.start <= d) && d <= ob.end;
  const isSundayDate = (d) => new Date(`${d}T00:00:00`).getDay() === 0;
  const placementLk = (r) => r.status !== "libur" && !(isSundayDate(r.date) && r.status === "tidak_absen" && !(Number(r.points) > 0));
  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => {
    if (j !== i) return r;
    const nr = { ...r, ...patch };
    // penempatan luar base lokasi: ganti status → luar kota ikut (YA kecuali libur)
    if (patch.status !== undefined && inPlacement(nr.date)) nr.luar_kota = placementLk(nr);
    return nr;
  }));
  const applyPlacement = () => {
    setRows((rs) => rs.map((r) => (inPlacement(r.date) ? { ...r, luar_kota: placementLk(r) } : r)));
    toast.success("Luar kota diterapkan sesuai penempatan — klik Simpan untuk menyimpan");
  };

  // Live compute (mirror server)
  const rates = data?.rates || {};
  const isStaff = data?.role === "staff";
  const toMin = (s) => {
    const p = (s || "").split(":");
    if (p.length !== 2) return null;
    const v = parseInt(p[0], 10) * 60 + parseInt(p[1], 10);
    return isNaN(v) ? null : v;
  };
  const NORMAL_MIN = 8 * 60 + 30;
  const fmtDur = (min) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, "0")}`;
  const calc = rows.map((r) => {
    const pts = Number(r.points) || 0;
    const uangHadir = r.status === "masuk" ? (rates.benefit_absen || 0) : r.status === "telat" ? TELAT_UANG_HADIR : 0;
    let jamKerjaMin = 0, lemburMin = 0, uangLembur = 0;
    if (isStaff && (r.status === "masuk" || r.status === "telat")) {
      const mi = toMin(r.jam_masuk), mo = toMin(r.jam_pulang);
      if (mi !== null && mo !== null && mo > mi) {
        jamKerjaMin = mo - mi;
        lemburMin = Math.max(jamKerjaMin - NORMAL_MIN, 0);
        uangLembur = Math.round((lemburMin / 60) * (rates.lembur_per_jam || 0));
      }
    }
    return {
      uangHadir,
      bonusRp: isStaff ? 0 : pts * (rates.bonus_per_point || 0),
      uangMakanAuto: isStaff ? 0 : (pts >= 2 ? (rates.benefit_makan_siang || 0) : 0),
      uangMakan: isStaff ? 0 : (r.uang_makan_manual !== null && r.uang_makan_manual !== undefined && r.uang_makan_manual !== ""
        ? Number(r.uang_makan_manual) || 0
        : (pts >= 2 ? (rates.benefit_makan_siang || 0) : 0)),
      lkRp: isStaff ? 0 : (r.luar_kota ? (rates.benefit_luar_kota || 0) : 0),
      jamKerjaMin, lemburMin, uangLembur,
    };
  });
  const hariKerja = rows.filter((r) => r.status === "masuk" || r.status === "telat" || (r.status === "tidak_absen" && (Number(r.points) || 0) > 0)).length;
  const s = data?.summary || {};
  const workdays = Math.max((s.days_in_month || 30) - (s.sundays || 0), 1);
  const gajiPokok = hariKerja >= 22 ? (rates.salary || 0) : hariKerja * ((rates.salary || 0) / workdays);
  const totHadir = calc.reduce((a, c) => a + c.uangHadir, 0);
  const totBonus = calc.reduce((a, c) => a + c.bonusRp, 0);
  const totMakan = calc.reduce((a, c) => a + c.uangMakan, 0);
  const totLk = calc.reduce((a, c) => a + c.lkRp, 0);
  const totLembur = calc.reduce((a, c) => a + c.uangLembur, 0);
  const totLemburMin = calc.reduce((a, c) => a + c.lemburMin, 0);
  const bonusVideoRp = Math.round((Number(totalVideo) || 0) * (rates.bonus_per_video || 0));
  const totalGaji = isStaff
    ? gajiPokok + totHadir + totLembur + bonusVideoRp + (rates.benefit_kesehatan || 0) + (rates.benefit_transport || 0)
      + (Number(manual.bonus_lainnya) || 0) + (Number(manual.bonus_dok_report) || 0)
    : gajiPokok + totHadir + totBonus + totMakan + totLk + (rates.benefit_kesehatan || 0) + (rates.benefit_service_motor || 0)
      + (Number(manual.bonus_lainnya) || 0) + (Number(manual.bonus_dok_report) || 0);
  const totalTransfer = totalGaji - (Number(potongan) || 0);

  const downloadSlip = async () => {
    await save(false);
    const token = localStorage.getItem("om_token");
    window.open(`${API}/payroll/slip.pdf?technician_id=${techId}&month=${month}&token=${token}`, "_blank");
  };

  const inputCls = "w-full px-2 py-1.5 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]";

  if (locked) {
    return (
      <Layout>
        <div className="max-w-md mx-auto mt-12 bg-white border border-[#E4E4E7]" data-testid="payroll-lock-screen">
          <div className="px-6 py-4 border-b border-[#E4E4E7]">
            <div className="label-cap">Akses Terproteksi</div>
            <h2 className="font-heading text-2xl font-bold">Payroll</h2>
          </div>
          <form onSubmit={submitUnlock} className="p-6 space-y-4">
            <p className="text-sm text-[#52525B]">Halaman ini diproteksi. Masukkan password pengaturan untuk mengelola payroll.</p>
            <input type="password" required autoFocus value={lockPwd} onChange={(e) => setLockPwd(e.target.value)} placeholder="Password pengaturan" data-testid="payroll-unlock-input" className="w-full px-3 py-2.5 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
            <button type="submit" disabled={unlocking} data-testid="payroll-unlock-button" className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              <Lock size={14} /> {unlocking ? "Memverifikasi…" : "Buka Payroll"}
            </button>
          </form>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="label-cap mb-2">Penggajian</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Payroll</h1>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] p-4 mb-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-cap block mb-1.5">Karyawan</label>
          <select value={techId} onChange={(e) => setTechId(e.target.value)} data-testid="payroll-tech-select" className="px-3 py-2 border border-[#E4E4E7] text-sm bg-white outline-none min-w-[200px]">
            <option value="">— Pilih Karyawan —</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-cap block mb-1.5">Bulan</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="payroll-month-input" className="px-3 py-2 border border-[#E4E4E7] text-sm font-mono outline-none" />
        </div>
        <button onClick={() => load(0)} disabled={loading} data-testid="payroll-load-button" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2 disabled:opacity-60">
          <FileSpreadsheet size={14} /> {loading ? "Memuat…" : "Muat Payroll"}
        </button>
        {data && (
          <button onClick={() => load(1)} disabled={loading} data-testid="payroll-refresh-button" title="Ambil ulang jam absensi & point order (edit manual akan ditimpa)" className="text-xs font-bold uppercase tracking-wider px-3 py-2.5 border border-[#E4E4E7] hover:bg-[#F4F4F5] flex items-center gap-2">
            <RefreshCw size={14} /> Muat Ulang dari Data
          </button>
        )}
      </div>

      {data && data.outside_base && (
        <div className="bg-[#7C3AED]/5 border border-[#7C3AED]/40 px-4 py-3 mb-6 flex flex-wrap items-center gap-3" data-testid="payroll-outside-base-notice">
          <div className="flex-1 min-w-[240px] text-sm">
            <span className="font-bold text-[#7C3AED]">Penempatan di luar basecamp{data.outside_base.base_location ? ` (${data.outside_base.base_location})` : ""}{data.outside_base.to_base ? ` → base ${data.outside_base.to_base}` : ""}</span>
            <span className="block text-[#52525B]">
              {data.technician_name} mendapat fasilitas di luar basecamp {data.outside_base.start ? `mulai ${data.outside_base.start} ` : ""}sampai <b>{data.outside_base.end}</b>.
              Setiap hari dihitung Luar Kota = YA, kecuali hari libur (Minggu tanpa kerja ikut libur).
            </span>
          </div>
          <button onClick={applyPlacement} data-testid="payroll-apply-outside-base" className="text-xs font-bold uppercase tracking-wider px-3 py-2 border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/10">
            Terapkan ke Tabel
          </button>
        </div>
      )}

      {data && (
        <>
          <div className="grid lg:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-[#E4E4E7]">
              <div className="px-4 py-3 border-b border-[#E4E4E7] flex justify-between">
                <span className="label-cap">Ringkasan — {data.technician_name}</span>
                <span className="label-cap">{month}</span>
              </div>
              <div className="p-4 space-y-1.5 text-sm">
                <SumRow label="Gaji Pokok" value={gajiPokok} testid="payroll-gaji-pokok" hint={hariKerja >= 22 ? "Full" : `Prorate ${hariKerja}×(${formatIDR(rates.salary || 0)}/${workdays})`} />
                <SumRow label="Total Uang Hadir" value={totHadir} />
                {isStaff ? (
                  <>
                    <SumRow label={`Total Uang Lembur (${fmtDur(totLemburMin)})`} value={totLembur} testid="payroll-total-lembur" />
                    <SumRow label={`Bonus Video (${Number(totalVideo) || 0} video)`} value={bonusVideoRp} testid="payroll-bonus-video" />
                    <SumRow label="Penggantian Uang Kesehatan" value={rates.benefit_kesehatan || 0} />
                    <SumRow label="Penggantian Transport" value={rates.benefit_transport || 0} />
                  </>
                ) : (
                  <>
                    <SumRow label="Total Bonus Point" value={totBonus} />
                    <SumRow label="Penggantian Uang Makan" value={totMakan} />
                    <SumRow label="Tunjangan Luar Kota" value={totLk} />
                    <SumRow label="Penggantian Uang Kesehatan" value={rates.benefit_kesehatan || 0} />
                    <SumRow label="Penggantian Uang Service Motor" value={rates.benefit_service_motor || 0} />
                  </>
                )}
              </div>
            </div>
            <div className="bg-white border border-[#E4E4E7]">
              <div className="px-4 py-3 border-b border-[#E4E4E7]"><span className="label-cap">Input Manual</span></div>
              <div className="p-4 space-y-2">
                {[["bonus_lainnya", "Bonus Lainnya", isStaff ? "" : "Otomatis 1,5% dari total nilai laporan teknisi bulan ini — masih bisa diubah manual"], ["bon_bensin", "Penggantian Bon Bensin", ""], ["bonus_dok_report", "Bonus Dokumentasi Report", ""]].map(([k, l, hint]) => (
                  <div key={k} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-[#52525B] flex-1">
                      {l}
                      {hint && <span className="block text-[10px] text-[#7C3AED]">{hint}</span>}
                    </span>
                    <input type="number" value={manual[k]} onChange={(e) => setManual({ ...manual, [k]: e.target.value })} data-testid={`payroll-manual-${k}`} className="w-32 px-2 py-1.5 border border-[#E4E4E7] text-sm font-mono text-right outline-none focus:border-[#002FA7]" />
                  </div>
                ))}
                {!isStaff && (
                  <div className="text-[11px] text-[#52525B] space-y-1 pt-1">
                    <div className="font-bold uppercase tracking-wider text-[10px]">Sumber Bon Bensin</div>
                    <div className="flex flex-wrap gap-2">
                      {[["reimburse", "Reimbursement Geo-Absen"], ["mileage", `Jarak Tempuh (${formatIDR(data.bon_bensin_mileage || 0)})`]].map(([v, l]) => (
                        <button key={v} type="button" data-testid={`payroll-bensin-source-${v}`} onClick={() => {
                          setBensinSource(v);
                          if (v === "mileage") {
                            setManual((m) => ({ ...m, bon_bensin: data.bon_bensin_mileage || 0 }));
                          } else {
                            const sum = (data.reimbursements || []).filter((x) => reimburseIds.includes(x.id)).reduce((a, x) => a + (Number(x.amount) || 0), 0);
                            setManual((m) => ({ ...m, bon_bensin: sum }));
                          }
                        }} className={`px-2.5 py-1.5 border text-[10px] font-bold uppercase ${bensinSource === v ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-[#E4E4E7] hover:bg-[#F4F4F5]"}`}>{l}</button>
                      ))}
                    </div>
                    {bensinSource === "mileage" && (
                      <div className="text-[10px] text-[#A1A1AA]">Otomatis dari halaman Jarak Tempuh (order teknisi utama, kendaraan motor, bulan ini). Nilai tetap bisa diedit manual di atas.</div>
                    )}
                  </div>
                )}
                {bensinSource === "reimburse" && (data.reimbursements || []).length > 0 && (
                  <div className="text-[11px] text-[#52525B] space-y-1 pt-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold uppercase tracking-wider text-[10px]">
                        Reimbursement Geo-Absen (Bon Bensin)
                        {data.reimbursements.some((r) => (r.flags || []).some((f) => f.type === "double_upload" || f.type === "double_struk")) && (
                          <span className="ml-2 text-[#B91C1C]">· ada bon ganda</span>
                        )}
                      </div>
                      <button type="button" data-testid="payroll-reimburse-gallery-open" onClick={() => setGalleryOpen(true)}
                        className="text-[10px] font-bold uppercase px-2 py-1 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white whitespace-nowrap">
                        Galeri Bon ({data.reimbursements.length})
                      </button>
                    </div>
                    <div data-testid="payroll-reimburse-list" className="border border-[#E4E4E7] bg-[#FAFAFA] max-h-52 overflow-y-auto divide-y divide-[#E4E4E7]/60">
                      {data.reimbursements.map((r) => {
                        const checked = reimburseIds.includes(r.id);
                        return (
                          <label key={r.id} className={`flex items-start gap-2 px-2 py-1.5 cursor-pointer ${reimburseRowTone(r.flags)}`}>
                            <input type="checkbox" data-testid={`payroll-reimburse-${r.id}`} checked={checked} onChange={(e) => {
                              const next = e.target.checked ? [...reimburseIds, r.id] : reimburseIds.filter((x) => x !== r.id);
                              setReimburseIds(next);
                              const sum = (data.reimbursements || []).filter((x) => next.includes(x.id)).reduce((a, x) => a + (Number(x.amount) || 0), 0);
                              setManual((m) => ({ ...m, bon_bensin: sum }));
                            }} className="mt-0.5 w-3.5 h-3.5 accent-[#002FA7]" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-mono">{r.date}</span>
                                <span className="font-mono font-bold whitespace-nowrap">{formatIDR(r.amount)}</span>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[#A1A1AA] truncate">{r.category}{r.remark ? ` · ${r.remark.replace(/\n/g, " ")}` : ""}</span>
                                {r.photo_url && <a href={r.photo_url} target="_blank" rel="noreferrer" data-testid={`payroll-reimburse-photo-${r.id}`} className="text-[#002FA7] font-bold whitespace-nowrap hover:underline">lihat bon ↗</a>}
                              </div>
                              <ReimburseFlags flags={r.flags} compact />
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <div className="text-[10px] text-[#A1A1AA]">Centang bon yang ditagihkan — jumlah otomatis mengisi "Penggantian Bon Bensin" (tetap bisa diedit manual).</div>
                  </div>
                )}
                {(data.bonus_dok_breakdown || []).length > 0 && (
                  <div className="text-[11px] text-[#52525B] space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span>Otomatis dari video Drive: <span className="font-mono font-bold">{formatIDR(data.bonus_dok_auto || 0)}</span></span>
                      <div className="flex items-center gap-3">
                        <button type="button" data-testid="payroll-video-detail-toggle" onClick={() => setShowVideoDetail((v) => !v)} className="text-[#52525B] font-bold uppercase text-[10px] hover:underline">{showVideoDetail ? "Tutup" : "Detail"}</button>
                        {(data.bonus_dok_auto || 0) > 0 && Number(manual.bonus_dok_report) !== data.bonus_dok_auto && (
                          <button type="button" data-testid="payroll-apply-video-bonus" onClick={() => setManual({ ...manual, bonus_dok_report: data.bonus_dok_auto })} className="text-[#002FA7] font-bold uppercase text-[10px] hover:underline">Pakai</button>
                        )}
                      </div>
                    </div>
                    {showVideoDetail && (
                      <div data-testid="payroll-video-breakdown" className="border border-[#E4E4E7] bg-[#FAFAFA] max-h-52 overflow-y-auto">
                        <table className="w-full text-[10px]">
                          <thead>
                            <tr className="text-left text-[#A1A1AA] uppercase border-b border-[#E4E4E7]">
                              <th className="px-2 py-1 font-bold">Order</th>
                              <th className="px-2 py-1 font-bold">Folder Drive</th>
                              <th className="px-2 py-1 font-bold text-right">Durasi</th>
                              <th className="px-2 py-1 font-bold text-right">Bonus</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.bonus_dok_breakdown.map((b, i) => (
                              <tr key={i} className="border-b border-[#E4E4E7]/60 last:border-0">
                                <td className="px-2 py-1">
                                  <div className="font-mono font-bold text-[#111113]">{b.order_no}</div>
                                  <div className="text-[#A1A1AA]">{b.customer_name || "-"}</div>
                                </td>
                                <td className="px-2 py-1">{b.folder_name ? b.folder_name : <span className="text-[#DC2626]">folder tidak ditemukan</span>}</td>
                                <td className="px-2 py-1 text-right font-mono whitespace-nowrap">
                                  {b.millis > 0 ? `${Math.floor(b.millis / 60000)}m ${Math.round((b.millis % 60000) / 1000)}d` : "-"}
                                  {b.video_count > 0 && <div className="text-[#A1A1AA]">{b.video_count} video · {b.minutes} mnt</div>}
                                </td>
                                <td className="px-2 py-1 text-right font-mono font-bold whitespace-nowrap">{b.bonus > 0 ? formatIDR(b.bonus) : "Rp 0"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="px-2 py-1 text-[#A1A1AA]">Rp 5.000/menit (dibulatkan ke atas), maks Rp 50.000 per order.</div>
                      </div>
                    )}
                  </div>
                )}
                {isStaff && (
                  <>
                    <div className="pt-2 border-t border-[#E4E4E7]">
                      <div className="text-[11px] font-bold text-[#52525B] uppercase tracking-wider mb-1.5">Platform dihitung dalam Bonus Video</div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {[["youtube", "YouTube Video"], ["short", "Shorts"], ["tiktok", "TikTok"], ["instagram", "Instagram"]].map(([k, l]) => {
                          const cnt = (data.video_counts || {})[k] || 0;
                          const checked = videoPlatforms.includes(k);
                          return (
                            <label key={k} className="flex items-center gap-1.5 cursor-pointer text-xs text-[#52525B]">
                              <input type="checkbox" data-testid={`payroll-video-platform-${k}`} checked={checked} onChange={(e) => {
                                const next = e.target.checked ? [...videoPlatforms, k] : videoPlatforms.filter((p) => p !== k);
                                setVideoPlatforms(next);
                                setTotalVideo(next.reduce((a, p) => a + ((data.video_counts || {})[p] || 0), 0));
                              }} className="w-3.5 h-3.5 accent-[#002FA7]" />
                              <span>{l} <span className="font-mono font-bold">({cnt})</span></span>
                            </label>
                          );
                        })}
                      </div>
                      <div className="text-[10px] text-[#A1A1AA] mt-1">Jumlah link diambil dari menu Content Sosmed (staff ini, bulan ini).</div>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-[#52525B] flex-1">Total Video Diupload</span>
                      <input type="number" min="0" value={totalVideo} onChange={(e) => setTotalVideo(e.target.value)} data-testid="payroll-total-video" className="w-32 px-2 py-1.5 border border-[#E4E4E7] text-sm font-mono text-right outline-none focus:border-[#002FA7]" />
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#E4E4E7]">
                  <span className="text-sm text-[#DC2626] font-bold flex-1">Potongan (Kasbon)</span>
                  <input type="number" value={potongan} onChange={(e) => setPotongan(e.target.value)} readOnly={!!data.kasbon_recorded} title={data.kasbon_recorded ? "Potongan sudah dicatat ke kasbon — ubah lewat Riwayat Pembayaran di menu Kasbon" : undefined} data-testid="payroll-potongan-input" className={`${data.kasbon_recorded ? "bg-[#F4F4F5] cursor-not-allowed " : ""}w-32 px-2 py-1.5 border border-[#DC2626]/40 text-sm font-mono text-right text-[#DC2626] outline-none focus:border-[#DC2626]`} />
                </div>
                <div className="text-xs text-[#52525B]">Sisa kasbon saat ini: <span className="font-mono font-bold">{formatIDR(s.sisa_kasbon || 0)}</span>{data.kasbon_recorded && <span className="ml-2 text-[#059669] font-bold">✓ potongan sudah dicatat ke kasbon (mengikuti riwayat pembayaran kasbon)</span>}</div>
                <div className="text-[11px] text-[#D97706]">* Penggantian bon bensin tidak masuk Total Di Transfer — dibayarkan setiap tanggal 15 bulan berikutnya.</div>
              </div>
            </div>
            <div className="bg-[#111113] text-white border border-[#111113]">
              <div className="px-4 py-3 border-b border-white/10"><span className="label-cap text-white/60">Total</span></div>
              <div className="p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-white/70">Total Gaji Karyawan</span><span className="font-mono font-bold" data-testid="payroll-total-gaji">{formatIDR(totalGaji)}</span></div>
                <div className="flex justify-between"><span className="text-white/70">Potongan</span><span className="font-mono text-red-400">− {formatIDR(Number(potongan) || 0)}</span></div>
                <div className="flex justify-between pt-2 border-t border-white/10"><span className="font-bold">TOTAL DI TRANSFER</span><span className="font-mono font-black text-lg text-[#FFD166]" data-testid="payroll-total-transfer">{formatIDR(totalTransfer)}</span></div>
                <div className="flex justify-between"><span className="text-white/50 text-xs">Bon Bensin (dibayar tgl 15 bln depan)</span><span className="font-mono text-white/70 text-xs">{formatIDR(Number(manual.bon_bensin) || 0)}</span></div>
                <div className="flex justify-between pt-2"><span className="text-white/70">Jumlah Hari Bekerja</span><span className="font-mono" data-testid="payroll-hari-kerja">{hariKerja} hari</span></div>
                <div className="flex justify-between"><span className="text-white/70">Total Lokasi (Order Selesai)</span><span className="font-mono">{s.total_lokasi || 0}</span></div>
                <div className="flex gap-2 pt-3">
                  <button onClick={() => save(false)} disabled={saving} data-testid="payroll-save-button" className="flex-1 bg-white text-[#111113] text-xs font-bold uppercase tracking-wider px-3 py-2.5 flex items-center justify-center gap-2 hover:bg-white/90 disabled:opacity-60">
                    <Save size={14} /> Simpan
                  </button>
                  {!data.kasbon_recorded && (Number(potongan) || 0) > 0 && (s.sisa_kasbon || 0) > 0 && (
                    <button onClick={() => save(true)} disabled={saving} data-testid="payroll-save-kasbon-button" title="Simpan payroll dan catat potongan sebagai pembayaran cicilan kasbon" className="flex-1 bg-[#059669] text-white text-xs font-bold uppercase tracking-wider px-3 py-2.5 flex items-center justify-center gap-2 hover:bg-[#047857] disabled:opacity-60">
                      <Banknote size={14} /> Simpan + Kasbon
                    </button>
                  )}
                </div>
                <button onClick={downloadSlip} disabled={saving} data-testid="payroll-slip-button" title="Simpan otomatis lalu buka slip gaji PDF" className="w-full mt-2 border border-[#FFD166] text-[#FFD166] text-xs font-bold uppercase tracking-wider px-3 py-2.5 flex items-center justify-center gap-2 hover:bg-[#FFD166]/10 disabled:opacity-60">
                  <FileText size={14} /> Slip Gaji PDF
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E4E4E7] overflow-x-auto">
            <table className="w-full min-w-[1100px]" data-testid="payroll-table">
              <thead>
                <tr className="border-b border-[#E4E4E7] bg-[#F4F4F5]">
                  {(isStaff
                    ? ["No", "Tanggal", "Hari", "Jam Masuk", "Jam Pulang", "Status", "Jam Kerja", "Durasi Lembur", "Uang Lembur", "Uang Hadir", "Remark"]
                    : ["No", "Tanggal", "Hari", "Jam Masuk", "Status", "Uang Hadir", "Total Point Harian", "Bonus Point (Rp)", "Uang Makan", "Luar Kota", "Remark"]
                  ).map((h) => (
                    <th key={h} className="text-left label-cap px-3 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const c = calc[i];
                  const sunday = r.day_name === "Minggu";
                  return (
                    <tr key={r.date} className={`border-b border-[#E4E4E7] ${sunday ? "bg-[#DC2626]/5" : ""}`} data-testid={`payroll-row-${r.date}`}>
                      <td className="px-3 py-2 text-sm font-mono text-[#52525B]">{i + 1}</td>
                      <td className="px-3 py-2 text-sm font-mono whitespace-nowrap">{r.date}</td>
                      <td className={`px-3 py-2 text-sm ${sunday ? "text-[#DC2626] font-bold" : ""}`}>{r.day_name}</td>
                      <td className="px-3 py-2">
                        <input value={r.jam_masuk} onChange={(e) => setRow(i, { jam_masuk: e.target.value })} placeholder="—" data-testid={`payroll-jam-${r.date}`} className={inputCls + " w-20 font-mono"} />
                      </td>
                      {isStaff && (
                        <td className="px-3 py-2">
                          <input value={r.jam_pulang || ""} onChange={(e) => setRow(i, { jam_pulang: e.target.value })} placeholder="—" data-testid={`payroll-pulang-${r.date}`} className={inputCls + " w-20 font-mono"} />
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <select value={r.status} onChange={(e) => setRow(i, { status: e.target.value })} data-testid={`payroll-status-${r.date}`} className={`px-2 py-1.5 border text-xs font-bold uppercase bg-white outline-none ${r.status === "masuk" ? "border-[#059669] text-[#059669]" : r.status === "telat" ? "border-[#D97706] text-[#D97706]" : r.status === "libur" ? "border-[#002FA7] text-[#002FA7]" : "border-[#E4E4E7] text-[#52525B]"}`}>
                          {STATUS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      </td>
                      {isStaff ? (
                        <>
                          <td className="px-3 py-2 text-sm font-mono text-right">{c.jamKerjaMin ? fmtDur(c.jamKerjaMin) : "—"}</td>
                          <td className="px-3 py-2 text-sm font-mono text-right">{c.lemburMin ? fmtDur(c.lemburMin) : "—"}</td>
                          <td className="px-3 py-2 text-sm font-mono text-right" data-testid={`payroll-lembur-${r.date}`}>{c.uangLembur ? formatIDR(c.uangLembur) : "—"}</td>
                          <td className="px-3 py-2 text-sm font-mono text-right">{c.uangHadir ? formatIDR(c.uangHadir) : "—"}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2 text-sm font-mono text-right">{c.uangHadir ? formatIDR(c.uangHadir) : "—"}</td>
                          <td className="px-3 py-2">
                            <input type="number" min="0" value={r.points} onChange={(e) => setRow(i, { points: e.target.value })} data-testid={`payroll-points-${r.date}`} className={inputCls + " w-16 font-mono text-right"} />
                          </td>
                          <td className="px-3 py-2 text-sm font-mono text-right">{c.bonusRp ? formatIDR(c.bonusRp) : "—"}</td>
                          <td className="px-3 py-2">
                            <input type="number" min="0" value={r.uang_makan_manual ?? ""} placeholder={String(c.uangMakanAuto || 0)} onChange={(e) => setRow(i, { uang_makan_manual: e.target.value === "" ? null : e.target.value })} title={(r.uang_makan_manual ?? "") !== "" ? `Diedit manual (otomatis: ${formatIDR(c.uangMakanAuto)}). Kosongkan untuk kembali otomatis.` : "Otomatis (point ≥ 2). Ketik angka untuk mengubah."} data-testid={`payroll-makan-${r.date}`} style={(r.uang_makan_manual ?? "") !== "" ? { borderColor: "#D97706", background: "#FFFBEB" } : undefined} className={inputCls + " w-24 font-mono text-right"} />
                          </td>
                          <td className="px-3 py-2">
                            <select value={r.luar_kota ? "ya" : "tdk"} onChange={(e) => setRow(i, { luar_kota: e.target.value === "ya" })} data-testid={`payroll-lk-${r.date}`} className={`px-2 py-1.5 border text-xs font-bold uppercase bg-white outline-none ${r.luar_kota ? "border-[#7C3AED] text-[#7C3AED]" : "border-[#E4E4E7] text-[#52525B]"}`}>
                              <option value="tdk">Tdk</option>
                              <option value="ya">Ya</option>
                            </select>
                          </td>
                        </>
                      )}
                      <td className="px-3 py-2">
                        <input value={r.remark} onChange={(e) => setRow(i, { remark: e.target.value })} data-testid={`payroll-remark-${r.date}`} className={inputCls + " min-w-[140px]"} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!data && (
        <div className="bg-white border border-[#E4E4E7] p-10 text-center text-sm text-[#52525B]">
          Pilih karyawan dan bulan, lalu klik <b>Muat Payroll</b>. Jam masuk diambil otomatis dari geo-absen (via NIK), point dari order Selesai.
        </div>
      )}
      {data && (
        <ReimburseGallery
          open={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          items={data.reimbursements || []}
          checkedIds={reimburseIds}
          techName={data.technician_name || ""}
          onRejected={onReimburseRejected}
        />
      )}
    </Layout>
  );
};

const SumRow = ({ label, value, testid, hint }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-[#52525B] flex-1">{label}{hint && <span className="block text-[10px] text-[#A1A1AA]">{hint}</span>}</span>
    <span className="font-mono font-bold" data-testid={testid}>{formatIDR(value)}</span>
  </div>
);

export default Payroll;
